import { onlineManager } from '@tanstack/react-query';
import type { QueryClient, Mutation } from '@tanstack/react-query';
import type { TRPCClient } from '@trpc/client';
import type { AppRouter } from '@servwell/api/router';

/**
 * Making queued work actually send.
 *
 * The app already paused mutations fired offline, already wrote them to IndexedDB, and
 * already called `resumePausedMutations()` when the network came back. What it did NOT do
 * was survive the app being closed — and that is the case that matters, because a tech in
 * a basement puts the phone in their pocket.
 *
 * Two things were missing, and together they lost the work silently:
 *
 *   1. Nothing resumed the queue after a RESTORE. `onlineManager.subscribe` fires on a
 *      connectivity change; opening the app fresh while already online is not a change.
 *   2. A mutation restored from IndexedDB has no `mutationFn`. Functions cannot be
 *      serialized, so `hydrate()` rebuilds the mutation from its key alone and looks the
 *      function up in the client's mutation defaults. With none registered, the restored
 *      mutation can never run.
 *
 * Verified before the fix: a tech taps "Finish this job" with no signal, the button reads
 * "Finishing…", the app is reloaded — and the visit is still In Progress with the
 * completion sitting in IndexedDB forever. Nothing in the console. The office sees an
 * unfinished job; the work is recorded nowhere.
 *
 * Replay is safe because every mutation carries a client-minted idempotency key
 * (Foundation Rule 3): the server dedupes on (clientId, mutationId), so a write that
 * actually landed before the connection dropped applies exactly once on replay.
 */

/**
 * The mutations a tech can fire with no signal, and therefore the ones that must survive
 * the app closing. tRPC's mutation key is `[[...pathParts]]`, so each entry here is the
 * procedure path.
 *
 * Deliberately a list, not "everything". A write is only worth keeping on the device if it
 * can be replayed: these get a durable row, under the account that made them, before their first attempt
 * (pending-writes.ts), and an office write fired offline fails visibly in the session instead
 * of being written to disk to be forgotten. Silent loss is the thing being fixed; a new kind
 * of it would be a poor trade.
 */
export const REPLAYABLE: readonly string[] = [
  // The visit machine — the tech's whole day runs through these four.
  'visit.enRoute',
  'visit.start',
  'visit.abort',
  'visit.complete',
  /**
   * "Running behind" on the next-stop card (117). ⚠️ **The one entry on this list that
   * expresses an intention about the FUTURE rather than recording something that HAPPENED.**
   *
   * Every other write here is true whenever it lands: a stop was started, a part came off
   * the van, a payment was taken. Replaying one late still records a fact. A reschedule
   * does not decay the same way — it says "put this stop at 2:45", and by the time a van
   * comes back into signal, dispatch may have moved the same visit themselves. The replay
   * would then overwrite the office's newer decision with a time computed from an older
   * one, and neither side would be told. `rescheduleVisit` has no optimistic concurrency to
   * catch it.
   *
   * ⭐ Listed anyway, and deliberately: the alternative is a tech tapping a control in a
   * basement and losing the change with nothing on screen, which is the exact failure this
   * whole module was written to end. The two hazards are not equal — a silently lost write
   * has no witness, while a stale overwrite lands on the office's board, where a dispatcher
   * is looking. It is a real trade and it is made in this direction on purpose.
   *
   * ⚠️ The server still refuses a stop that has since moved past `scheduled`
   * (`whyNotReschedulable`), so a tech who queued a nudge and then started the job gets a
   * visible refusal in the failed-write log rather than a rewritten history.
   */
  'visit.reschedule',
  // Time on the job, which feeds costing and, on a T&M job, billing (ticket 13). Each carries
  // the moment of its tap (`tap()`, build-map 062), so a late replay records when it happened.
  'costing.startTimer',
  'costing.stopTimer',
  // What happened at the door.
  'note.append',
  'checklist.setItem',
  'payment.recordManual',
  // Parts off the van.
  'inventory.useOnJob',
  'inventory.returnFromJob',
  'inventory.transfer',
  'restocks.check',
  // Equipment at the address.
  'equipment.create',
  'equipment.update',
  'equipment.tag',
  'equipment.untag',
  'equipment.retire',
  // Custom fields the shop added to its own forms.
  'customField.create',
  'customField.update',
  'customField.set',
  'customField.remove',
  'attachment.remove',
  /**
   * A problem found at the door (ops insights Phase 5a, ticket 03): "Flag a problem" and a
   * message on a ticket of the tech's job. Both are database only (R-7: the bell is a row).
   * A flag names the ticket by an id minted on the phone, so its replay names the same ticket,
   * and its photos wait for it in the upload queue (`ticketPhotoAwaitsItsFlag`).
   */
  'ticket.flag',
  'ticket.message',
];

/** tRPC mutation keys are `[[...path]]`; flatten one back to "visit.complete". */
export function pathOf(mutationKey: unknown): string | null {
  if (!Array.isArray(mutationKey)) return null;
  const parts = mutationKey.flat(2).filter((p): p is string => typeof p === 'string');
  return parts.length ? parts.join('.') : null;
}

/**
 * The saved query cache holds no write at all (MAP 225b).
 *
 * It used to hold every paused replayable write, which TanStack's `hydrate()` rebuilt at the
 * next start for whoever was signed in by then. Each replayable write now has its own row,
 * stamped with the account that made it and saved before its first attempt
 * (pending-writes.ts), and is rebuilt only for that account. An earlier release's saved writes are moved out of the blob once
 * (legacy-writes.ts).
 */
export function shouldDehydrateMutation(): boolean {
  return false;
}

/**
 * Give every replayable path a `mutationFn` that can be found by key after a restore.
 *
 * The function walks the tRPC client by the path in the key — `visit.complete` becomes
 * `client.visit.complete.mutate(input)` — which is exactly what `mutationOptions()` builds
 * at call time, so a replayed write is indistinguishable from the original.
 *
 * `onLanded` runs whenever a replayable write lands, by any route, with the mutation's
 * `meta` (a rebuilt one carries its row's `draftKey` there). It is the one success hook a
 * write the QUEUE delivered has (one rebuilt from its row: a paused copy, or restored at a
 * start), which no screen observes; tRPC's options run it after a screen's own `onSuccess`
 * too.
 */
export function registerReplay(
  queryClient: QueryClient,
  trpcClient: TRPCClient<AppRouter>,
  opts: { onLanded?: (path: string, variables: unknown, meta: unknown) => void } = {},
): void {
  for (const path of REPLAYABLE) {
    const parts = path.split('.');
    const send = (input: unknown) => {
      let node: Record<string, unknown> = trpcClient as never;
      for (const part of parts) node = node[part] as Record<string, unknown>;
      return (node as unknown as { mutate: (i: unknown) => Promise<unknown> }).mutate(
        input,
      );
    };
    queryClient.setMutationDefaults([parts], {
      // A restored completion is the case that most needs the ordering: a whole offline
      // session replays at once, with no screen mounted and nobody to notice a refusal.
      mutationFn:
        path === ORDERED_LAST
          ? (orderVisitCompletion(queryClient, send as never) as never)
          : send,
      onSuccess: (
        _data: unknown,
        variables: unknown,
        _result: unknown,
        context: { meta?: unknown } | undefined,
      ) => opts.onLanded?.(path, variables, context?.meta),
    });
  }
}

// ── Which writes were done with no signal ────────────────────────────────────

/**
 * The mutations that have been paused at some point in this session.
 *
 * The banner needs this and TanStack does not keep it: a mutation that paused offline and
 * then resumed is, by the time it is on the wire, indistinguishable from one fired online a
 * second ago. Without the distinction the banner can only count `isPaused` — and `isPaused`
 * clears **the instant the queue resumes, before anything has been delivered**, which is how
 * *"Everything you did offline has gone through"* came to fire at un-pause. Counting plain
 * `sending` instead would fix that and break the other end: every ordinary online save would
 * flash a bar announcing offline work that never happened.
 *
 * Keyed by `Mutation['mutationId']`, which is TanStack's own per-instance counter and not the
 * client-minted idempotency `mutationId` in the write's variables. Pruned on removal so the
 * set cannot outlive the cache.
 */
const queuedOffline = new Set<number>();

/** Whether this mutation was ever waiting for a network in this session. */
export function wasQueuedOffline(mutationId: number): boolean {
  return queuedOffline.has(mutationId);
}

/**
 * Start recording them. Registered during `ApiProvider`'s first render, **before** the
 * persister hydrates — a restored mutation arrives already paused, and if nothing is
 * listening yet it is invisible to the banner for the rest of the session.
 */
export function trackOfflineWrites(queryClient: QueryClient): () => void {
  return queryClient.getMutationCache().subscribe((event) => {
    const mutation = event.mutation;
    if (!mutation) return;
    if (event.type === 'removed') {
      queuedOffline.delete(mutation.mutationId);
      return;
    }
    if (mutation.state.isPaused) queuedOffline.add(mutation.mutationId);
  });
}

/** Test seam. The set is module state, and one test's writes must not colour the next's. */
export function __resetOfflineWrites(): void {
  queuedOffline.clear();
}

// ── Ordering a visit's completion behind its own writes ──────────────────────

/**
 * `assertRequiredItemsDone` runs inside `completeVisit`'s transaction, so a completion that
 * reaches the server before the ticks that permit it is CORRECTLY refused — hours later, on
 * reconnect, for a reason the tech cannot see, cause or fix. Everything below exists to make
 * that ordering real instead of lucky.
 *
 * ⚠️ **The obvious place to put this is the wrong place, and it took a test to show it.**
 * The first attempt replaced `resumePausedMutations()` with a resume that withheld
 * `continue()` from a completion until its visit's other writes had settled. It controls
 * nothing, because **the app's call is not the only one**: `QueryClientProvider` calls
 * `client.mount()`, and `QueryClient.mount()` subscribes to `onlineManager` and calls
 * `resumePausedMutations()` itself (`queryClient.js`). So the queue resumes on the
 * connectivity event whether or not anything in this app does — verified, a mutation with
 * no `continue()` call of ours sends the instant the network returns. A resume-ordering fix
 * passes every ordering assertion you can write about resuming and changes nothing about
 * what actually goes out.
 *
 * *(Ticket 014 recorded this as `createRetryer` subscribing each paused mutation itself.
 * That is not what the source does — the retryer has no `onlineManager` subscription at all
 * — and the distinction matters, because the real owner is a subscription inside the client
 * that this app cannot intercept. Corrected here rather than left as a mechanism a later
 * reader might try to unhook.)*
 *
 * So the barrier lives in the WRITE, at the one moment that is common to every path into it:
 * the mutation function. Whoever started it — the online event, a restore, a manual retry —
 * the completion does not go out while its own visit still has writes outstanding, nor before
 * a write for another visit that was queued first (`writesACompletionFollows` says why).
 */
const ORDERED_LAST = 'visit.complete';

/**
 * A write a completion may wait on: one with no answer yet, whether still waiting for a network
 * or already in flight; never another completion.
 *
 * ⚠️ Not `isPaused`. A write queued in a live session keeps `isPaused` through its resumed
 * attempt, but one rebuilt from its row at a restart replays with `isPaused` already false
 * (LESSONS 2026-08-11). Counted by `isPaused`, a restored write still in flight was invisible
 * here, so the completion resumed beside it went first anyway: the overtaking this barrier
 * exists to stop, in the one case it matters most (a whole offline session replayed at once).
 * "No answer yet" holds for both until the write settles or leaves the cache.
 */
function waitableWrite(m: Mutation): boolean {
  if (m.state.status !== 'pending') return false;
  // A completion never waits on another completion: two for one visit is a double tap, and
  // the screen already refuses that. Excluding them is also what makes waiting safe to call
  // from inside a completion — it cannot await itself, nor two completions each other.
  return pathOf(m.options.mutationKey) !== ORDERED_LAST;
}

const visitOf = (m: Mutation): string | null => {
  const visitId = (m.state.variables as { visitId?: unknown } | undefined)?.visitId;
  return typeof visitId === 'string' ? visitId : null;
};

/** Writes still on the phone for this visit, other than a completion. */
export function outstandingVisitWrites(
  queryClient: QueryClient,
  visitId: string,
): Mutation[] {
  return queryClient
    .getMutationCache()
    .findAll({ predicate: (m) => waitableWrite(m) && visitOf(m) === visitId });
}

/**
 * Was `m` queued before `own`? By when each was first submitted, which a write rebuilt from its
 * row keeps (`submittedAt: row.createdAt`, pending-writes.ts), and by TanStack's own counter
 * for two in the same millisecond (rows are rebuilt oldest first). A completion that is not in
 * the cache is being sent now, after everything already queued.
 */
function queuedBefore(m: Mutation, own: Mutation | undefined): boolean {
  if (!own) return true;
  const [mine, theirs] = [own.state.submittedAt, m.state.submittedAt];
  return theirs < mine || (theirs === mine && m.mutationId < own.mutationId);
}

/**
 * Every write a completion must not overtake: all of its own visit's, and every one queued
 * BEFORE it for any other visit.
 *
 * ⚠️ **Why other visits too.** Completing a visit can put the tech's NEXT visit en route on the
 * server, in the same transaction (`putNextVisitEnRoute` in the api). A tech who taps "Running
 * behind?" or "On my way" for their next stop and then finishes this one, all with no signal,
 * has queued those in that order; if the completion lands first, the next stop is already en
 * route when the older write arrives, which refuses it into the failed-write log for something
 * the tech did right. Waiting for what was queued first replays the day in the order it was
 * lived. A write queued AFTER the completion is not waited for, and neither is one that names no
 * visit (parts, timers): neither can be overtaken in a way the completion changes.
 *
 * `completion` is the completion's own variables, which is how its mutation is found in the
 * cache (the same object TanStack hands the mutation function).
 */
export function writesACompletionFollows(
  queryClient: QueryClient,
  completion: { visitId: string },
): Mutation[] {
  const cache = queryClient.getMutationCache();
  const own = cache
    .getAll()
    .find(
      (m) =>
        m.state.variables === completion &&
        pathOf(m.options.mutationKey) === ORDERED_LAST,
    );
  const earlierElsewhere = cache.findAll({
    predicate: (m) => {
      if (!waitableWrite(m)) return false;
      const visitId = visitOf(m);
      return visitId !== null && visitId !== completion.visitId && queuedBefore(m, own);
    },
  });
  return [
    ...outstandingVisitWrites(queryClient, completion.visitId),
    ...earlierElsewhere,
  ];
}

/** A mutation is done when it has an answer — success or failure, both are settled. */
const isSettled = (m: Mutation): boolean => m.state.status !== 'pending';

/**
 * How long a completion will wait for the writes it follows before going anyway.
 *
 * Bounded on purpose. Waiting forever would turn a stuck prerequisite into a completion that
 * is never sent at all — silent loss, which is the failure this whole area exists to prevent
 * and is strictly worse than the race. Sending late degrades to today's behaviour: the server
 * is still the gate, and still refuses correctly.
 */
const MAX_WAIT_MS = 30_000;

/** Resolve once every write this completion follows has an answer. */
export function awaitVisitWrites(
  queryClient: QueryClient,
  completion: { visitId: string },
): Promise<void> {
  // Offline, do not wait — fall straight through to the send, which fails and pauses the
  // mutation exactly as before. Waiting here instead would hold the completion in `sending`
  // rather than `queued`, and its button would read "Finishing…" with no signal — the very
  // lie Wave 0.2 removed.
  if (!onlineManager.isOnline()) return Promise.resolve();

  const outstanding = writesACompletionFollows(queryClient, completion);
  if (outstanding.length === 0) return Promise.resolve();

  return new Promise<void>((resolve) => {
    let unsubscribe: (() => void) | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      if (timer) clearTimeout(timer);
      unsubscribe?.();
      resolve();
    };
    const check = () => {
      // A mutation garbage-collected out of the cache is settled as far as we are concerned.
      const live = queryClient.getMutationCache().getAll();
      if (outstanding.every((m) => isSettled(m) || !live.includes(m))) finish();
    };
    timer = setTimeout(finish, MAX_WAIT_MS);
    unsubscribe = queryClient.getMutationCache().subscribe(check);
    check(); // they may already be settled between the findAll and the subscribe
  });
}

/**
 * Wrap `visit.complete`'s send so it waits for the writes it follows (`writesACompletionFollows`).
 *
 * Applied at BOTH the live call site and the restored-mutation default, because those are the
 * only two ways this write is ever made and they are built differently — the page gets its
 * function from tRPC's `mutationOptions()`, a restored mutation gets it from
 * `setMutationDefaults`. `replay-order.test.ts` asserts both, so a third path cannot appear
 * without one of them failing.
 */
export function orderVisitCompletion<
  TInput extends { visitId: string },
  TResult,
  // TanStack v5 hands the mutation function a context as a SECOND argument. Typing this as
  // a one-argument function compiles at the default registration and silently drops it at
  // the tRPC call site, so the rest is passed through rather than swallowed.
  TRest extends unknown[],
>(
  queryClient: QueryClient,
  send: (input: TInput, ...rest: TRest) => Promise<TResult>,
): (input: TInput, ...rest: TRest) => Promise<TResult> {
  return async (input: TInput, ...rest: TRest) => {
    await awaitVisitWrites(queryClient, input);
    return send(input, ...rest);
  };
}
